import { MODAL_SOURCE } from '../../analytics.ts';

import type {
	ConnectorPromoBannerSelection,
	ConnectorPromoBannerVariant,
	GdriveIneligibleReason,
	OrgIneligibleReason,
} from './connectorPromoBannerModel.ts';

export const PROMO_BANNER_ACTION_SUBJECT_ID = 'connectorPromoBanner';

/** The gdrive banner reports `google` to line up with the org banner's trait keys. */
const GDRIVE_ANALYTICS_CONNECTOR = 'google';

export type PromoBannerAttributes = {
	bannerType: ConnectorPromoBannerVariant;
	/**
	 * Set whenever the Drive variant did not win, so a `generic` or `orgConnectedApps`
	 * banner still says why Drive was ruled out. Absent on the Drive variant itself.
	 */
	gdriveIneligibleReason?: GdriveIneligibleReason;
	/** Likewise for the org variant; absent when the org variant won. */
	orgIneligibleReason?: OrgIneligibleReason;
	/** Absent on the generic variant, which is about no app in particular. */
	connector?: string;
	/** Only set on the org variant. */
	topAppPercent?: number;
	/** Icons on screen when the event fired; they can arrive after it. */
	ghostIconCount: number;
};

export const getBannerShownAttributes = (
	selection: ConnectorPromoBannerSelection,
	ghostIconCount: number,
): PromoBannerAttributes => {
	if (selection.variant === 'gdrive') {
		return { bannerType: 'gdrive', connector: GDRIVE_ANALYTICS_CONNECTOR, ghostIconCount };
	}
	if (selection.variant === 'orgConnectedApps') {
		return {
			bannerType: 'orgConnectedApps',
			connector: selection.topApp.traitKey,
			topAppPercent: selection.topApp.percent,
			gdriveIneligibleReason: selection.gdriveIneligibleReason,
			ghostIconCount,
		};
	}
	return {
		bannerType: 'generic',
		gdriveIneligibleReason: selection.gdriveIneligibleReason,
		orgIneligibleReason: selection.orgIneligibleReason,
		ghostIconCount,
	};
};

type PromoBannerUIEvent<TAction extends string, TActionSubject extends string> = {
	readonly type: 'sendUIEvent';
	readonly data: {
		readonly action: TAction;
		readonly actionSubject: TActionSubject;
		readonly actionSubjectId: 'connectorPromoBanner';
		readonly source: 'teamworkGraphModal';
		readonly attributes: PromoBannerAttributes;
	};
};

export const getBannerShownEvent = (
	attributes: PromoBannerAttributes,
): PromoBannerUIEvent<'viewed', 'banner'> =>
	({
		type: 'sendUIEvent',
		data: {
			action: 'viewed',
			actionSubject: 'banner',
			actionSubjectId: PROMO_BANNER_ACTION_SUBJECT_ID,
			source: MODAL_SOURCE,
			attributes,
		},
	}) as const;

export const getBannerClickedEvent = (
	attributes: PromoBannerAttributes,
): PromoBannerUIEvent<'clicked', 'banner'> =>
	({
		type: 'sendUIEvent',
		data: {
			action: 'clicked',
			actionSubject: 'banner',
			actionSubjectId: PROMO_BANNER_ACTION_SUBJECT_ID,
			source: MODAL_SOURCE,
			attributes,
		},
	}) as const;

/**
 * The whole surface is suppressed, as opposed to a variant losing — the banner has
 * nothing to connect to when the dialog it opens is unavailable.
 */
export type BannerNotShownReason = 'rovoDisabled';

export type BannerNotShownAttributes = {
	reason: BannerNotShownReason;
};

type PromoBannerOperationalEvent<TAction extends string, TActionSubject extends string> = {
	readonly type: 'sendOperationalEvent';
	readonly data: {
		readonly action: TAction;
		readonly actionSubject: TActionSubject;
		readonly actionSubjectId: 'connectorPromoBanner';
		readonly source: 'teamworkGraphModal';
		readonly attributes: BannerNotShownAttributes;
	};
};

export const getBannerNotShownEvent = (
	attributes: BannerNotShownAttributes,
): PromoBannerOperationalEvent<'notShown', 'banner'> =>
	({
		type: 'sendOperationalEvent',
		data: {
			action: 'notShown',
			actionSubject: 'banner',
			actionSubjectId: PROMO_BANNER_ACTION_SUBJECT_ID,
			source: MODAL_SOURCE,
			attributes,
		},
	}) as const;
